import { ExpandMoreRounded as ExpandIcon } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";
import Component from "components/Component";
import Button from "components/common/Button";
import ExposureSelector from "components/common/ChartedExposureSelector";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import Visible from "components/common/Visible";
import * as labels from "config.labels";
import * as _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as staticDataService from "services/staticDataService";
import * as baseLayerBurnStore from "store/baseLayerBurn";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as submissionSelector from "store/selectors/submission/submissionSelector";

const useTriggerPricing = ({ updateBaseLayerBurn }) => {
  const {
    data: config,
    isLoading: isConfigLoading,
  } = staticDataService.useConfigQuery("analytics");

  const [
    _triggerPricing,
    { isFetching: isPricingFetching },
  ] = analyticsService.useLazySubmissionAnalyticsQuery();

  const triggerPricing = useCallback(
    async (submission) => {
      if (isConfigLoading) {
        return;
      }
      const request = {
        engine: config?.programPricing?.engine ?? "base_layer_burn",
        metric: "program_pricing",
        submission,
      };
      const response = await _triggerPricing(request);
      updateBaseLayerBurn({
        pricingCalculation: {
          response: response?.data,
        },
      });
    },
    [isConfigLoading, config, updateBaseLayerBurn, _triggerPricing]
  );

  return {
    triggerPricing,
    isPricingFetching,
    isConfigLoading,
  };
};

const extractTrigger = (baseLayerBurn) =>
  Object.fromEntries(
    Object.entries(baseLayerBurn ?? {}).filter(([key]) =>
      [
        "limit",
        "attachment",
        "scalingExposureKey",
        "startDate",
        "endDate",
      ].includes(key)
    )
  );

const useAutocalc = ({ baseLayerBurn, triggerPricing, isConfigLoading }) => {
  const submission = useSelector(
    submissionSelector.selectResolvedSubmissionWithoutInput
  );

  const latestTrigger = extractTrigger(baseLayerBurn);

  const [lastCalcedTrigger, setLastCalcedTrigger] = useState(latestTrigger);

  useEffect(() => {
    if (isConfigLoading) {
      return;
    }
    if (!_.isEqual(latestTrigger, lastCalcedTrigger)) {
      setLastCalcedTrigger(latestTrigger);
      setTimeout(() => triggerPricing(submission), 0);
    }
  }, [
    isConfigLoading,
    submission,
    latestTrigger,
    lastCalcedTrigger,
    setLastCalcedTrigger,
    triggerPricing,
  ]);
};

const CalculateProgramPricingButton = ({
  triggerPricing,
  isConfigLoading,
  isPricingFetching,
}) => {
  const submission = useSelector(
    submissionSelector.selectResolvedSubmissionWithoutInput
  );

  return (
    <Button
      onClick={() => triggerPricing(submission)}
      isLoading={isPricingFetching}
      disabled={isConfigLoading}
    >
      {"Calculate"}
    </Button>
  );
};

const Pricing = ({ disabled, updateBaseLayerBurn }) => {
  const dispatch = useDispatch();
  const baseLayerBurn = useSelector(baseLayerBurnStore.selectBaseLayerBurn);
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const {
    triggerPricing,
    isConfigLoading,
    isPricingFetching,
  } = useTriggerPricing({ updateBaseLayerBurn });

  useAutocalc({
    baseLayerBurn,
    triggerPricing,
    isConfigLoading,
  });

  return (
    <Component
      title={translateLabel(labels.BASE_LAYER_BURN_PRICING_TITLE, {
        default: "Base Layer Pricing",
      })}
      subtitle={translateLabel(labels.BASE_LAYER_BURN_PRICING_SUBTITLE, {
        default: null,
      })}
      loading={isConfigLoading}
      isFetching={isPricingFetching}
    >
      <Stack direction={"column"} gap={3}>
        <Visible byTag={"<BaseLayerBurn>.scalingExposure"} defaultVisible>
          <ExposureSelector
            value={baseLayerBurn?.scalingExposureKey}
            onChange={(scalingExposureKey) => {
              dispatch(baseLayerBurnStore.update({ scalingExposureKey }));
            }}
            fullWidth
            label={translateLabel(
              labels.BASE_LAYER_BURN_SCALING_EXPOSURE_LABEL,
              {
                default: "Scale by Exposure",
              }
            )}
          />
        </Visible>
        <Visible byTag={"<BaseLayerBurn>.lossesByYear"} defaultVisible>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Typography variant={"subtitle1"}>
                {translateLabel(labels.BASE_LAYER_BURN_LOSSES_BY_YEAR_LABEL, {
                  default: "Losses by Year",
                })}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align={"right"} width={80}>
                      {"Year"}
                    </TableCell>
                    <TableCell align={"right"} width={200}>
                      {"GU Losses"}
                    </TableCell>
                    <TableCell align={"right"} width={200}>
                      {"Base Layer Losses"}
                    </TableCell>
                    <TableCell align={"right"} width={120}>
                      {"Year Fraction"}
                    </TableCell>
                    {baseLayerBurn?.scalingExposureKey != null && (
                      <TableCell align={"right"} width={200}>
                        {"Exposure"}
                      </TableCell>
                    )}
                    <TableCell>{/* Empty space */}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {baseLayerBurn?.pricing?.values?.lossesByYear?.map(
                    (losses) => (
                      <TableRow key={losses.year}>
                        <TableCell align={"right"}>{losses.year}</TableCell>
                        <TableCell align={"right"}>
                          <NumberFormat
                            displayType={"text"}
                            thousandSeparator={true}
                            defaultValue={""}
                            decimalScale={0}
                            className={"monospace"}
                            fixedDecimalScale
                            value={losses.guLosses ?? ""}
                          />
                        </TableCell>
                        <TableCell align={"right"}>
                          <NumberFormat
                            displayType={"text"}
                            thousandSeparator={true}
                            defaultValue={""}
                            decimalScale={0}
                            className={"monospace"}
                            fixedDecimalScale
                            value={losses.baseLayerLosses ?? ""}
                          />
                        </TableCell>
                        <TableCell align={"right"}>
                          <NumberFormat
                            displayType={"text"}
                            thousandSeparator={true}
                            defaultValue={""}
                            decimalScale={2}
                            suffix={"%"}
                            className={"monospace"}
                            fixedDecimalScale
                            value={
                              losses.yearFraction != null
                                ? 100 * losses.yearFraction
                                : ""
                            }
                          />
                        </TableCell>
                        {baseLayerBurn?.scalingExposureKey != null && (
                          <TableCell align={"right"}>
                            <NumberFormat
                              displayType={"text"}
                              thousandSeparator={true}
                              defaultValue={""}
                              decimalScale={0}
                              className={"monospace"}
                              fixedDecimalScale
                              value={losses.exposure ?? ""}
                            />
                          </TableCell>
                        )}
                        <TableCell />
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        </Visible>
        <Table>
          <TableBody>
            <Visible
              byTag={"<BaseLayerBurn>.pricingTable.simpleBurn"}
              defaultVisible
            >
              <TableRow>
                <TableCell width={200} align={"right"}>
                  <Typography variant={"subtitle2"}>
                    {translateLabel(labels.BASE_LAYER_BURN_SIMPLE_BURN_LABEL, {
                      default: "Simple Burn",
                    })}
                  </Typography>
                </TableCell>
                <TableCell width={250} align={"right"}>
                  <NumberFormat
                    displayType={"text"}
                    thousandSeparator={true}
                    defaultValue={""}
                    decimalScale={0}
                    className={"monospace"}
                    fixedDecimalScale
                    value={
                      baseLayerBurn?.pricing?.values?.simpleBaseLayerBurn ?? ""
                    }
                  />
                </TableCell>
                <TableCell />
              </TableRow>
            </Visible>
            <Visible
              byTag={"<BaseLayerBurn>.pricingTable.lossPerUnitExposure"}
              defaultVisible
            >
              <TableRow>
                <TableCell width={200} align={"right"}>
                  <Typography variant={"subtitle2"}>
                    {translateLabel(
                      labels.BASE_LAYER_BURN_LOSS_PER_UNIT_EXPOSURE_LABEL,
                      {
                        default: "Loss per Unit Exposure",
                      }
                    )}
                  </Typography>
                </TableCell>
                <TableCell width={250} align={"right"}>
                  <NumberFormat
                    displayType={"text"}
                    thousandSeparator={true}
                    defaultValue={""}
                    decimalScale={5}
                    className={"monospace"}
                    value={
                      baseLayerBurn?.pricing?.values?.lossPerUnitExposure ?? ""
                    }
                  />
                </TableCell>
                <TableCell />
              </TableRow>
            </Visible>
            <Visible
              byTag={"<BaseLayerBurn>.pricingTable.exposureScaledBurn"}
              defaultVisible
            >
              <TableRow>
                <TableCell width={200} align={"right"}>
                  <Typography variant={"subtitle2"}>
                    {translateLabel(
                      labels.BASE_LAYER_BURN_EXPOSURE_SCALED_BURN_LABEL,
                      {
                        default: "Exposure-Scaled Burn",
                      }
                    )}
                  </Typography>
                </TableCell>
                <TableCell width={250} align={"right"}>
                  <NumberFormat
                    displayType={"text"}
                    thousandSeparator={true}
                    defaultValue={""}
                    decimalScale={0}
                    className={"monospace"}
                    fixedDecimalScale
                    value={
                      baseLayerBurn?.pricing?.values
                        ?.exposureScaledBaseLayerBurn ?? ""
                    }
                  />
                </TableCell>
                <TableCell />
              </TableRow>
            </Visible>
            <Visible
              byTag={"<BaseLayerBurn>.pricingTable.selectedPrice"}
              defaultVisible
            >
              <TableRow>
                <TableCell width={200} align={"right"}>
                  <Typography variant={"subtitle2"}>
                    {translateLabel(
                      labels.BASE_LAYER_BURN_SELECTED_PRICE_LABEL,
                      {
                        default: "Selected Price",
                      }
                    )}
                  </Typography>
                </TableCell>
                <TableCell width={250} align={"right"}>
                  <PrettyNumberTextField
                    variant={"filled"}
                    hiddenLabel
                    size={"small"}
                    fullWidth
                    disabled={disabled}
                    value={baseLayerBurn?.selectedBaseLayerPrice}
                    onChangeNumber={(baseLayerPriceOverride) =>
                      updateBaseLayerBurn({ baseLayerPriceOverride })
                    }
                    inputProps={{ style: { textAlign: "right" } }}
                    allowClear={baseLayerBurn?.baseLayerPriceOverride != null}
                    offFormatter={numbers.dpString(0)}
                  />
                </TableCell>
                <TableCell />
              </TableRow>
            </Visible>
          </TableBody>
        </Table>
        <Stack
          direction={"row"}
          justifyContent={"flex-end"}
          alignItems={"top"}
          width={"100%"}
          spacing={2}
        >
          {baseLayerBurn?.pricing?.failure && (
            <Alert severity={"error"} sx={{ width: "100%" }}>
              {baseLayerBurn?.pricing?.failure}
            </Alert>
          )}
          <CalculateProgramPricingButton
            triggerPricing={triggerPricing}
            isConfigLoading={isConfigLoading}
            isPricingFetching={isPricingFetching}
          />
        </Stack>
      </Stack>
    </Component>
  );
};

export default Pricing;
